import {Dayjs} from 'dayjs';
import { LIMITS } from '../contract';

export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly';

export type RecurrenceRule = {
    frequency: RecurrenceFrequency;
    interval: number;    // every N days / weeks / months
    weekdays: number[];  // 0 (Sun) – 6 (Sat); only used when weekly
    end: { type: 'count', count: number } | { type: 'until', until: Dayjs };
};

// The server's cap (from the API contract); a series is always bounded.
export const MAX_RECURRING_OCCURRENCES = LIMITS.recurringOccurrences;

// Expands a rule into concrete occurrence times, starting at `start` and keeping
// its time of day. Works in the user's local time zone so a weekday or month
// boundary can't land on the wrong calendar day.
export function generateOccurrences(start: Dayjs, rule: RecurrenceRule): Dayjs[] {
    const limit = rule.end.type === 'count' ? Math.min(rule.end.count, MAX_RECURRING_OCCURRENCES) : MAX_RECURRING_OCCURRENCES;
    const lastAllowed = rule.end.type === 'until' ? rule.end.until.endOf('day') : null;
    const interval = Math.max(1, Math.floor(rule.interval));
    const results: Dayjs[] = [];

    // Returns false once the series is complete (limit reached or past the end date).
    const accept = (occurrence: Dayjs) => {
        if (lastAllowed && occurrence.isAfter(lastAllowed)) return false;
        results.push(occurrence);
        return results.length < limit;
    };

    if (rule.frequency === 'weekly') {
        const weekdays = [...new Set(rule.weekdays)].sort((a, b) => a - b);
        if (weekdays.length === 0) return results;

        const firstWeek = start.startOf('week');
        for (let week = 0; ; week += interval) {
            for (const weekday of weekdays) {
                const occurrence = firstWeek.add(week, 'week').add(weekday, 'day')
                    .hour(start.hour()).minute(start.minute()).second(start.second()).millisecond(start.millisecond());

                if (occurrence.isBefore(start)) continue; // earlier in the first week than the start
                if (!accept(occurrence)) return results;
            }
        }
    }

    const unit = rule.frequency === 'daily' ? 'day' : 'month';
    for (let step = 0; ; step++) {
        // Always counted from the original start so e.g. Jan 31 -> Feb 28 -> Mar 31.
        if (!accept(start.add(step * interval, unit))) return results;
    }
}
